import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { HttpClient } from '../../../HttpClient';
import { OutreachTypes, SignalApiUrls } from '../../../urls';
import {
  AutonomousMonitorEmailOutreach,
  ConfigurationCTAUrlPayload,
  ConfigurationCTAUrlResponse,
  ConfigurationEmailPayload,
  ConfigurationEmailResponse,
  MessageTypes,
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachSendTestSupportMessagePayload,
  OutreachSubscriberConfigResponse,
  SaveOutreachConfigDto,
  SaveOutreachWebhookConfig,
  UpdateOutreachConfigDto,
} from '../types';

export default class OutreachClient extends HttpClient {
  async saveOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.post<AxiosResponse<void>>(
      SignalApiUrls.getSaveOutreachConfigUrl(
        partnerId,
        monitorId,
        OutreachTypes.Email,
      ),
      payload,
    );
  }
  async saveOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
    payload: SaveOutreachWebhookConfig,
  ): Promise<void> {
    await this.post<AxiosResponse<void>>(
      SignalApiUrls.getSaveOutreachConfigUrl(
        partnerId,
        monitorId,
        OutreachTypes.Webhooks,
      ),
      payload,
    );
  }

  async saveOutreachPushConfig(
    partnerId: string,
    monitorId: string,
    payload: SaveOutreachConfigDto,
  ): Promise<void> {
    await this.post<AxiosResponse<void>>(
      SignalApiUrls.getSaveOutreachConfigUrl(
        partnerId,
        monitorId,
        OutreachTypes.Push,
      ),
      payload,
    );
  }

  async updateOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
    payload: UpdateOutreachConfigDto,
  ): Promise<void> {
    await this.patch<AxiosResponse<void>>(
      SignalApiUrls.getUpdateOutreachConfigUrl(
        partnerId,
        monitorId,
        outreachId,
        OutreachTypes.Email,
      ),
      payload,
    );
  }

  async updateOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
    payload: UpdateOutreachConfigDto,
  ): Promise<void> {
    await this.patch<AxiosResponse<void>>(
      SignalApiUrls.getUpdateOutreachConfigUrl(
        partnerId,
        monitorId,
        outreachId,
        OutreachTypes.Webhooks,
      ),
      payload,
    );
  }

  async updateOutreachPushConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
    payload: UpdateOutreachConfigDto,
  ): Promise<void> {
    await this.patch<AxiosResponse<void>>(
      SignalApiUrls.getUpdateOutreachConfigUrl(
        partnerId,
        monitorId,
        outreachId,
        OutreachTypes.Push,
      ),
      payload,
    );
  }

  async deleteOutreachPushConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
  ): Promise<void> {
    await this.delete<AxiosResponse<void>>(
      SignalApiUrls.getDeleteOutreachConfigUrl(
        partnerId,
        monitorId,
        outreachId,
        OutreachTypes.Push,
      ),
    );
  }

  async deleteOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
  ): Promise<void> {
    await this.delete<AxiosResponse<void>>(
      SignalApiUrls.getDeleteOutreachConfigUrl(
        partnerId,
        monitorId,
        outreachId,
        OutreachTypes.Email,
      ),
    );
  }

  async deleteOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
    outreachId: string,
  ): Promise<void> {
    await this.delete<AxiosResponse<void>>(
      SignalApiUrls.getDeleteOutreachConfigUrl(
        partnerId,
        monitorId,
        outreachId,
        OutreachTypes.Webhooks,
      ),
    );
  }

  async retrieveOutreachEmailConfig(
    partnerId: string,
    monitorId: string,
  ): Promise<OutreachConfigResponse[]> {
    const response = await this.get<AxiosResponse<OutreachConfigResponse[]>>(
      SignalApiUrls.getRetrieveOutreachConfigUrl(
        partnerId,
        monitorId,
        OutreachTypes.Email,
      ),
    );
    return response.data;
  }

  async retrieveOutreachWebhookConfig(
    partnerId: string,
    monitorId: string,
  ): Promise<OutreachConfigResponseWebhook[]> {
    const response = await this.get<
      AxiosResponse<OutreachConfigResponseWebhook[]>
    >(
      SignalApiUrls.getRetrieveOutreachConfigUrl(
        partnerId,
        monitorId,
        OutreachTypes.Webhooks,
      ),
    );
    return response.data;
  }

  async retrieveOutreachPushConfig(
    partnerId: string,
    monitorId: string,
  ): Promise<OutreachSubscriberConfigResponse[]> {
    const response = await this.get<
      AxiosResponse<OutreachSubscriberConfigResponse[]>
    >(
      SignalApiUrls.getRetrieveOutreachConfigUrl(
        partnerId,
        monitorId,
        OutreachTypes.Push,
      ),
    );
    return response.data;
  }

  async saveEmail(
    partnerId: string,
    email: string,
  ): Promise<ConfigurationEmailResponse> {
    const response = await this.post<AxiosResponse<ConfigurationEmailResponse>>(
      SignalApiUrls.getEmailConfigUrl(partnerId),
      {
        email,
        name: email,
      },
    );
    return response.data;
  }

  async saveEmailWithName(
    partnerId: string,
    email: ConfigurationEmailPayload,
  ): Promise<ConfigurationEmailResponse> {
    const response = await this.post<AxiosResponse<ConfigurationEmailResponse>>(
      SignalApiUrls.getEmailConfigUrl(partnerId),
      email,
    );
    return response.data;
  }

  async updateEmail(
    partnerId: string,
    email: ConfigurationEmailResponse,
  ): Promise<ConfigurationEmailResponse> {
    const response = await this.patch<
      AxiosResponse<ConfigurationEmailResponse, ConfigurationEmailPayload>
    >(SignalApiUrls.getEmailUpdateConfigUrl(partnerId, email.id), {
      email: email.email,
      name: email.name,
      language: email.language || 'en',
    });
    return response.data;
  }

  async deleteEmail(
    partnerId: string,
    emailId: string,
  ): Promise<ConfigurationEmailResponse> {
    const response = await this.delete<
      AxiosResponse<ConfigurationEmailResponse, ConfigurationEmailPayload>
    >(SignalApiUrls.getEmailUpdateConfigUrl(partnerId, emailId));
    return response.data;
  }

  async retrieveEmails(
    partnerId: string,
  ): Promise<ConfigurationEmailResponse[]> {
    const response = await this.get<
      AxiosResponse<ConfigurationEmailResponse[], ConfigurationEmailPayload>
    >(SignalApiUrls.getEmailConfigUrl(partnerId));
    return response.data;
  }

  async saveCTAUrl(
    partnerId: string,
    urlLink: string,
  ): Promise<ConfigurationCTAUrlResponse> {
    const response = await this.post<
      AxiosResponse<ConfigurationCTAUrlResponse, ConfigurationCTAUrlPayload>
    >(SignalApiUrls.getCTAUrlConfigUrl(partnerId), {
      urlLink,
    });
    return response.data;
  }

  async updateCTAUrl(
    partnerId: string,
    url: ConfigurationCTAUrlResponse,
  ): Promise<ConfigurationCTAUrlResponse> {
    const response = await this.patch<
      AxiosResponse<ConfigurationCTAUrlResponse, ConfigurationCTAUrlPayload>
    >(SignalApiUrls.getCTAUrlUpdateConfigUrl(partnerId, url.id), {
      urlLink: url.urlLink,
    });
    return response.data;
  }

  async deleteCTAUrl(
    partnerId: string,
    urlId: string,
  ): Promise<ConfigurationCTAUrlResponse> {
    const response = await this.delete<
      AxiosResponse<ConfigurationCTAUrlResponse, ConfigurationCTAUrlPayload>
    >(SignalApiUrls.getCTAUrlUpdateConfigUrl(partnerId, urlId));
    return response.data;
  }

  async retrieveCTAUrls(
    partnerId: string,
  ): Promise<ConfigurationCTAUrlResponse[]> {
    const response = await this.get<
      AxiosResponse<ConfigurationCTAUrlResponse[], ConfigurationCTAUrlPayload>
    >(SignalApiUrls.getCTAUrlConfigUrl(partnerId));
    return response.data;
  }

  async sendTestMessage<T extends MessageTypes>(
    partnerId: string,
    monitorId: string,
    payload: OutreachSendTestSupportMessagePayload<T>,
    messageType: MessageTypes,
    outreachType: OutreachTypes = OutreachTypes.Email,
  ): Promise<ConfigurationEmailResponse> {
    const response = await this.post<AxiosResponse<any>>(
      SignalApiUrls.getSendTestMessageUrl(
        partnerId,
        monitorId,
        outreachType,
        messageType,
      ),
      payload,
    );
    return response.data;
  }

  async getAutonomousMonitorEmailOutreach(
    partnerId: string,
    monitorId: string,
  ) {
    return await this.get<AxiosResponse<AutonomousMonitorEmailOutreach[]>>(
      SignalApiUrls.getAutonomousMonitorEmailOutreach(partnerId, monitorId),
    );
  }

  async patchAutonomousMonitorEmailOutreach(
    partnerId: string,
    monitorId: string,
    payload: AutonomousMonitorEmailOutreach,
  ) {
    return await this.patch<AxiosResponse<AutonomousMonitorEmailOutreach>>(
      SignalApiUrls.patchAutonomousMonitorEmailOutreach(
        partnerId,
        monitorId,
        payload.outreachId,
      ),
      _.omit(payload, 'outreachId'),
    );
  }

  async testAutonomousMonitorEmailOutreach(data: {
    partnerId: string;
    monitorId: string;
    recipients: string[];
    language?: string;
  }) {
    return await this.post<AxiosResponse<void>>(
      SignalApiUrls.testAutonomousMonitorEmailOutreach(
        data.partnerId,
        data.monitorId,
      ),
      { recipients: data.recipients, language: data.language },
    );
  }
}
